<template>
  <MessageBox
    v-if="messageProps"
    inline
    :summary="messageProps?.summary"
    :severity="messageProps?.severity"
    style="border-radius: 0;"
  >
    <span>
      {{ messageProps?.details }}
      <span
        style="text-decoration: underline;"
      >
        <a
          v-if="!licenseStore.licenseResponse?.isValid"
          href="#"
          @click="isModalVisible = true"
        >
          {{ i18n.global.t(`${I18N_PREFIX}.enterLicense`) }}
        </a>
        <router-link
          v-else-if="userHasWritePermission"
          :to="{
            name: ERouteName.SETTINGS_LICENSE,
          }"
        >
          {{ i18n.global.t(`${I18N_PREFIX}.enterLicense`) }}
        </router-link>
      </span>
    </span>
  </MessageBox>
  <PrmDialog
    v-if="licenseStore.isLicenseError"
    :visible="isModalVisible || isFatalLicenseViolation"
    modal
    :closable="!isFatalLicenseViolation"
    :draggable="false"
    :header="messageProps!.summary"
    style="min-width: 90dvh;"
    @update:visible="isModalVisible = $event"
  >
    <div class="flex-column-section">
      <MessageBox
        :summary="messageProps!.summary"
        :severity="messageProps!.severity"
        :details="messageProps!.details"
        style="width: 100%;"
      />
      <div class="input-wrapper">
        {{ i18n.global.t(`${I18N_PREFIX}.enterLicense`) }}
        <FormLicenseKey
          :mode="EFormMode.CREATE"
          @on-license-key-update="onLicenseKeyUpdate"
        />
      </div>
      <div
        v-if="!isFatalLicenseViolation"
        class="flex-row"
        style="justify-content: flex-end;"
      >
        <QmulusButton
          :label="i18n.global.t(`${I18N_PREFIX}.ignore`)"
          severity="danger"
          @click="isModalVisible = false"
        />
      </div>
    </div>
  </PrmDialog>
</template>

<script setup lang="ts">
import PrmDialog from '@NitrexGroup/primevue/dialog';
import dayjs from '@dayjs';
import {
  i18n,
} from '@i18n';
import QmulusButton from '@qmulus/ui/components/button/qmulus-button.vue';
import MessageBox from '@qmulus/ui/components/message-box/message-box.vue';
import {
  EFormMode,
} from '@qmulus/ui/types/form.ts';
import {
  computed, shallowRef, watchEffect,
} from 'vue';
import {
  useRouter,
} from 'vue-router';

import FormLicenseKey from '@/components/form-license-key/form-license-key.vue';
import {
  ERouteName,
} from '@/router/route-name.ts';
import {
  useAppStore,
} from '@/store/pinia/app.ts';
import {
  useLicenseStore,
} from '@/store/pinia/license.ts';
import {
  EAppId,
} from '@/types/app-type.ts';
import {
  formatDayjsDate,
} from '@/utils/date-formats.ts';
import {
  EAppPermission,
} from '@/utils/permission.ts';

const I18N_PREFIX = 'components/app/app-license-check';

const licenseStore = useLicenseStore();
licenseStore.loadLicense();
const appStore = useAppStore();
const router = useRouter();

const isModalVisible = shallowRef<boolean>(true);
const userHasWritePermission = computed<boolean>(() => appStore.getUserHasPermission(EAppId.TENANT_SETTINGS, EAppPermission.WRITE));

const isFatalLicenseViolation = computed<boolean>(() => {
  if (licenseStore.isLicenseError) {
    return [
      'NO_LICENSE_KEY',
      'EXPIRED',
    ].includes(licenseStore.licenseError!);
  }
  return false;
});

type TMessageProps = {
  severity: 'info' | 'warning' | 'danger',
  summary: string,
  details: string,
};
const messageProps = computed<TMessageProps | undefined>(() => {
  if (!licenseStore.isLicenseError) {
    if (licenseStore.license && licenseStore.license.expiry !== 'unlimited') {
      const dayjsExpiryDate = dayjs(licenseStore.license.expiry);
      if (dayjsExpiryDate.isBetween(dayjs(), dayjs().add(licenseStore.license?.expiryWarningDays || 14, 'days'))) {
        return {
          severity: 'info',
          summary: i18n.global.t(`${I18N_PREFIX}.expiresSoon.summary`),
          details: `${i18n.global.t(`${I18N_PREFIX}.expiresSoon.details`, {
            date: formatDayjsDate(dayjsExpiryDate),
          })} ${i18n.global.t(`${I18N_PREFIX}.contactSales`)}`,
        };
      }
    }
    return undefined;
  }

  const translationData = {
    expiryGracePeriodDays: licenseStore.license?.expiryGracePeriodDays || 31,
  };
  const message: TMessageProps = {
    severity: isFatalLicenseViolation.value ? 'danger' : 'warning',
    summary: i18n.global.t(`api.QmulusLicenseViolationReason.${licenseStore.licenseError!}`, translationData),
    details: i18n.global.t(`${I18N_PREFIX}.details.${licenseStore.licenseError}`, translationData),
  };

  message.details += ` ${i18n.global.t(`${I18N_PREFIX}.contactSales`)}`;

  return message;
});

watchEffect(
  () => {
    // Navigate to a page that does not contain any visible data in case the license is
    // invalid. If we go to Home, we might still show some data, we don't want to show
    if (isFatalLicenseViolation.value) {
      router.replace({
        name: ERouteName.APP_OVERVIEW,
      });
    }
  },
);

function onLicenseKeyUpdate() {
  window.location.reload();
}
</script>
